/**
 * FP3-035B-3 — Service Fidelity Overview.
 *
 * Shared component used by both surfaces:
 *   - Admin:   /admin/service-fidelity → <ServiceFidelityOverview mode="admin" />
 *   - Provider: /reports/service-fidelity → <ServiceFidelityOverview mode="provider" />
 *
 * Renders a calm, planning-tool style page:
 *   • Period switcher (week / month / custom) with prev / next nudge.
 *   • Filter bar (admin: provider / school / grade / eligibility /
 *     domain / status / search; provider: status / domain / search).
 *   • Aggregate summary tiles (matched / under / make-up / catch-up minutes).
 *   • Row table with status chips, target / delivered / catch-up.
 *
 * Wording is intentionally calm — this surface helps providers plan
 * make-up sessions, not call them out.
 *
 * Data contract: GET /api/service-fidelity?scope=...&period_type=...
 *   - Returns aggregate + sorted rows (worst status first).
 *   - Each row is (student × direct-service commitment × assigned
 *     provider). Eval-team-only assignments are pre-filtered server-
 *     side; provider mode pre-filters to the caller.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= on student-row navigation.
import useShadowNavigate from "@/lib/useShadowNavigate";
import { CaretLeft, CaretRight, MagnifyingGlass, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const STATUS_META = {
  met: { label: "Matched", className: "bg-[#E8F0EB] text-[#1E3F33] border-[#1E3F33]/20" },
  over: { label: "Over", className: "bg-[#E6EFF7] text-[#1F3A5A] border-[#1F3A5A]/20" },
  behind: { label: "Under", className: "bg-[#FAE9DC] text-[#7A4622] border-[#7A4622]/20" },
  on_track: { label: "On track", className: "bg-[#E6EFF7] text-[#1F3A5A] border-[#1F3A5A]/20" },
  met_after_exclusions: {
    label: "Met after exclusions",
    className: "bg-[#F1EEF6] text-[#4A3F66] border-[#4A3F66]/20",
  },
  needs_makeup: {
    label: "Needs make-up",
    className: "bg-[#FAF1DD] text-[#7A5B1F] border-[#7A5B1F]/20",
  },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, className: "bg-[#F3F1EC] text-[#6B7280]" };
  return (
    <Badge
      variant="outline"
      data-testid={`fidelity-status-${status}`}
      className={`text-[10px] font-semibold tracking-wide uppercase ${meta.className}`}
    >
      {meta.label}
    </Badge>
  );
}

function PeriodSwitcher({ periodType, setPeriodType, asOf, shiftAsOf, periodLabel }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="inline-flex rounded-lg border border-[#E5E3DB] bg-white overflow-hidden">
        {[
          { key: "week", label: "Week" },
          { key: "month", label: "Month" },
        ].map((opt) => (
          <button
            key={opt.key}
            data-testid={`fidelity-period-${opt.key}`}
            onClick={() => setPeriodType(opt.key)}
            className={`px-3 py-1.5 text-xs font-medium transition ${
              periodType === opt.key
                ? "bg-[#1E3F33] text-white"
                : "text-[#1F2937] hover:bg-[#F4F2EC]"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="inline-flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => shiftAsOf(-1)}
          data-testid="fidelity-period-prev"
          className="h-8 w-8 p-0"
        >
          <CaretLeft size={14} weight="bold" />
        </Button>
        <div
          data-testid="fidelity-period-label"
          className="text-sm font-medium text-[#1F2937] min-w-[180px] text-center"
        >
          {periodLabel}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => shiftAsOf(1)}
          data-testid="fidelity-period-next"
          className="h-8 w-8 p-0"
        >
          <CaretRight size={14} weight="bold" />
        </Button>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => shiftAsOf(0)}
        data-testid="fidelity-period-today"
        className="h-8 text-xs"
      >
        Today
      </Button>
    </div>
  );
}

function SummaryTile({ label, value, sublabel, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border bg-white px-3 py-2 ${
        accent || "border-[#E5E3DB]"
      }`}
    >
      <div className="text-[10px] uppercase tracking-wide text-[#6B7280] font-medium">
        {label}
      </div>
      <div className="text-xl font-semibold text-[#1F2937] tabular-nums">
        {value}
      </div>
      {sublabel && (
        <div className="text-[10px] text-[#6B7280] mt-0.5">{sublabel}</div>
      )}
    </div>
  );
}

export default function ServiceFidelityOverview({ mode = "auto" }) {
  const navigate = useShadowNavigate();
  // FP3-053 — Read super-admin context from URL so the report
  // scopes to the sidebar-pinned org/school.
  const [searchParams] = useSearchParams();
  const asOrg = (searchParams.get("as_org") || "").trim();
  const asSchool = (searchParams.get("as_school") || "").trim();
  // Period state — as_of drives prev/next; periodType toggles week/month.
  const [periodType, setPeriodType] = useState("week");
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));

  // Filter state.
  const [filters, setFilters] = useState({
    provider_id: "",
    school: "",
    grade: "",
    eligibility: "",
    domain: "",
    status: "",
    search: "",
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Filter option lookups (admin).
  const [providers, setProviders] = useState([]);
  const [schools, setSchools] = useState([]);
  const [eligibilityCategories, setEligibilityCategories] = useState([]);

  // Calendar shift — week=±7 days, month=±~30 days. 0 means snap to today.
  const shiftAsOf = useCallback(
    (delta) => {
      if (delta === 0) {
        setAsOf(new Date().toISOString().slice(0, 10));
        return;
      }
      const d = new Date(asOf);
      if (periodType === "week") {
        d.setDate(d.getDate() + delta * 7);
      } else {
        d.setMonth(d.getMonth() + delta);
      }
      setAsOf(d.toISOString().slice(0, 10));
    },
    [asOf, periodType],
  );

  // Filter option fetchers (admin only — we still fire them in
  // provider mode but it's harmless if they fail).
  useEffect(() => {
    if (mode === "provider") return; // skip — provider doesn't need provider/school pickers
    api.getProviders()
      .then((r) => setProviders(r.data?.providers || r.data || []))
      .catch(() => setProviders([]));
    api.getSchools?.()
      .then?.((r) => setSchools(r.data?.schools || r.data || []))
      ?.catch?.(() => setSchools([]));
    api.getEligibilityCategories()
      .then((r) => setEligibilityCategories(r.data?.categories || []))
      .catch(() => setEligibilityCategories([]));
  }, [mode]);

  // Main fetch.
  useEffect(() => {
    setLoading(true);
    setError("");
    const params = {
      scope: mode,
      period_type: periodType,
      as_of: asOf,
    };
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    // FP3-053 — Pass selected context. Backend ignores for non-
    // super-admin so this stays a no-op outside platform owners.
    if (asOrg) params.as_org = asOrg;
    if (asSchool) params.as_school = asSchool;
    api.getServiceFidelity(params)
      .then((r) => setData(r.data))
      .catch((e) => {
        setError(e?.response?.data?.detail || "Could not load service fidelity");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [mode, periodType, asOf, filters, asOrg, asSchool]);

  const aggregate = data?.aggregate || {};
  const rows = data?.rows || [];

  const summaryTiles = useMemo(() => {
    const a = aggregate;
    return [
      {
        testid: "fidelity-summary-matched",
        label: "Matched",
        value: a.met || 0,
        sublabel: `${a.over || 0} over · ${a.on_track || 0} on track`,
        accent: "border-[#1E3F33]/20",
      },
      {
        testid: "fidelity-summary-attention",
        label: "Needs attention",
        value: a.behind || 0,
        sublabel: `${a.on_track || 0} on track`,
        accent: "border-[#7A4622]/20",
      },
      {
        testid: "fidelity-summary-makeup",
        label: "Needs make-up",
        value: a.needs_makeup || 0,
        sublabel: `${a.met_after_exclusions || 0} met after exclusions`,
        accent: "border-[#7A5B1F]/20",
      },
      {
        testid: "fidelity-summary-minutes",
        label: "Delivered minutes",
        value: `${a.total_delivered_minutes || 0} / ${a.total_target_minutes || 0}`,
        sublabel: `${a.total_catchup_minutes || 0} catch-up minutes`,
        accent: "border-[#1F3A5A]/20",
      },
    ];
  }, [aggregate]);

  const handleRowClick = (row) => {
    if (!row?.student_id) return;
    navigate(`/students/${row.student_id}#tracker`);
  };

  const clearFilters = () => {
    setFilters({
      provider_id: "",
      school: "",
      grade: "",
      eligibility: "",
      domain: "",
      status: "",
      search: "",
    });
  };
  const anyFilterActive = Object.values(filters).some((v) => v);

  return (
    <div
      className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1600px] mx-auto space-y-4"
      data-testid="service-fidelity-overview"
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1F2937] heading-font">
            Service Fidelity
          </h1>
          <p className="text-sm text-[#6B7280] mt-0.5">
            {mode === "admin"
              ? "Direct-service commitments across the organization. Use filters to focus on a provider, school, or status."
              : "Your direct-service caseload by week or month. Click a row to open the student's Tracker tab."}
          </p>
        </div>
      </header>

      <PeriodSwitcher
        periodType={periodType}
        setPeriodType={setPeriodType}
        asOf={asOf}
        shiftAsOf={shiftAsOf}
        periodLabel={data?.period_label || "—"}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {summaryTiles.map((t) => (
          <SummaryTile key={t.testid} {...t} />
        ))}
      </div>

      {/* Filter bar */}
      <Card className="p-3 border-[#E5E3DB] bg-white">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <MagnifyingGlass
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-[#9B9890]"
            />
            <Input
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search student"
              data-testid="fidelity-filter-search"
              className="pl-7 h-9 text-sm w-56"
            />
          </div>
          {mode === "admin" && (
            <select
              value={filters.provider_id}
              onChange={(e) => setFilters({ ...filters, provider_id: e.target.value })}
              data-testid="fidelity-filter-provider"
              className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white"
            >
              <option value="">All providers</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.first_name} {p.last_name}
                  {p.role ? ` · ${p.role}` : ""}
                </option>
              ))}
            </select>
          )}
          {mode === "admin" && schools.length > 0 && (
            <select
              value={filters.school}
              onChange={(e) => setFilters({ ...filters, school: e.target.value })}
              data-testid="fidelity-filter-school"
              className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white"
            >
              <option value="">All schools</option>
              {schools.map((s) => (
                <option key={s.id || s.name} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
          <select
            value={filters.grade}
            onChange={(e) => setFilters({ ...filters, grade: e.target.value })}
            data-testid="fidelity-filter-grade"
            className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white"
          >
            <option value="">All grades</option>
            {Array.from({ length: 13 }, (_, i) => String(i)).map((g) => (
              <option key={g} value={g}>{g === "0" ? "K (0)" : `Gr ${g}`}</option>
            ))}
          </select>
          {mode === "admin" && eligibilityCategories.length > 0 && (
            <select
              value={filters.eligibility}
              onChange={(e) => setFilters({ ...filters, eligibility: e.target.value })}
              data-testid="fidelity-filter-eligibility"
              className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white"
            >
              <option value="">All eligibility</option>
              {eligibilityCategories.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          )}
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            data-testid="fidelity-filter-status"
            className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white"
          >
            <option value="">All statuses</option>
            <option value="met">Matched</option>
            <option value="over">Over</option>
            <option value="behind">Under</option>
            <option value="on_track">On track</option>
            <option value="met_after_exclusions">Met after exclusions</option>
            <option value="needs_makeup">Needs make-up</option>
          </select>
          {anyFilterActive && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              data-testid="fidelity-filter-clear"
              className="h-9 text-xs text-[#6B7280]"
            >
              <X size={12} className="mr-1" /> Clear
            </Button>
          )}
        </div>
      </Card>

      {/* Rows */}
      <Card className="border-[#E5E3DB] bg-white overflow-hidden">
        <div className="overflow-x-auto" data-testid="fidelity-rows-scroll">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F4F2EC] text-[#6B7280] uppercase tracking-wide text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">Student</th>
                <th className="px-3 py-2 text-left">Gr</th>
                <th className="px-3 py-2 text-left">Campus</th>
                {mode === "admin" && (
                  <>
                    <th className="px-3 py-2 text-left">Primary</th>
                    <th className="px-3 py-2 text-left">Assigned</th>
                  </>
                )}
                <th className="px-3 py-2 text-left">Service</th>
                <th className="px-3 py-2 text-right">Target</th>
                <th className="px-3 py-2 text-right">Delivered</th>
                <th className="px-3 py-2 text-right">Excused</th>
                <th className="px-3 py-2 text-right">Make-up</th>
                <th className="px-3 py-2 text-right">Catch-up</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={mode === "admin" ? 12 : 10} className="px-3 py-6 text-center text-xs text-[#9B9890]">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={mode === "admin" ? 12 : 10} className="px-3 py-6 text-center text-xs text-[#B85C5C]">
                    {error}
                  </td>
                </tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr>
                  <td colSpan={mode === "admin" ? 12 : 10} className="px-3 py-6 text-center text-xs text-[#9B9890]">
                    No direct-service commitments in this period.
                  </td>
                </tr>
              )}
              {!loading && !error && rows.map((r, i) => (
                <tr
                  key={`${r.student_id}-${r.assigned_provider_id}-${r.service_type}-${i}`}
                  data-testid={`fidelity-row-${r.student_id}-${i}`}
                  onClick={() => handleRowClick(r)}
                  className="border-t border-[#ECEAE4] hover:bg-[#FBFAF6] cursor-pointer"
                >
                  <td className="px-3 py-2">
                    <p className="font-medium text-[#1F2937]">
                      {r.first_name} {r.last_name}
                    </p>
                    {r.readable_id && (
                      <p className="text-[10px] text-[#9B9890] tabular-nums">{r.readable_id}</p>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums">{r.grade || "—"}</td>
                  <td className="px-3 py-2 text-[12px] text-[#4B5563]">{r.campus || "—"}</td>
                  {mode === "admin" && (
                    <>
                      <td className="px-3 py-2 text-[12px] text-[#4B5563]">{r.primary_provider_name || "—"}</td>
                      <td className="px-3 py-2 text-[12px] text-[#4B5563]">
                        {r.assigned_provider_name || "—"}
                        {r.assigned_provider_role && (
                          <span className="ml-1 text-[10px] text-[#9B9890]">· {r.assigned_provider_role}</span>
                        )}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2 text-[12px] text-[#1F2937]">{r.service_label || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.target_minutes}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.delivered_minutes}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4A3F66]">{r.excused_no_service_minutes}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#7A5B1F]">{r.make_up_needed_minutes}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.catchup_minutes > 0 ? r.catchup_minutes : "—"}
                  </td>
                  <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-[10px] text-[#9B9890] italic">
        Calculations respect provider block schedules, school calendar exceptions,
        and excused / make-up unavailables. Evaluation-team-only assignments
        (e.g. School Psychologist) are excluded from direct-service fidelity.
      </p>
    </div>
  );
}
